/* eslint-disable @typescript-eslint/no-unused-vars */
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { applyDisplayFilterFields } from "../../../../actions/Tenants/config/applyDisplayFilter";
import useConfig from "../../../../actions/Tenants/config/configHook";
import { useFields, usePagination, useUserPreferences } from "../../../../hooks";
import { layerListQueryKey, useLayerList } from "../../../../hooks/useLayerList";
import { useTenantNavigate } from "../../../../utils/tenantUrl";

import { FILTER_METHODS, META_FILTER_SOURCES, prepareParams } from "../../../../actions/Layers/actions";
import { defaultFilter, LAYER_TAB_CATEGORIES, LAYER_TABS, LayerFilter } from "../../../../actions/Layers/constants";
import { Tab } from "../../../../actions/Tenants/config/constantsTyped";
import { getFilledArrayOrDefault, isFilledArray } from "../../../../utils";
import useAuthorization from "../../../../utils/authorization";
import { streamAvosApi } from "../../../../utils/useAvosApiHook";
import { useDebounce } from "../../../Forms/utils";
import { useGroupBy } from "../../../Helper/GroupByModal";
import { attributeIsSet } from "../../../scenes/Tenants/Forms/flagConstants";
import { useRipeningPlanning, useRipeningPlanningDateRange } from "../../Ripening/hooks/useRipeningPlanning";
import { QC_STATUS } from "../Layer/Report/QCStatusButton";
import Field from "./Field";
import type { PersonalTabSettings } from "./switchTabContext";
import { SwitchTabContext } from "./switchTabContext";

export { useSwitchTabContext } from "./switchTabContext";
export type { PersonalTabSettings, SwitchTabContextProps } from "./switchTabContext";


const isValidDayNumber = (value) => value === 0 || value > 0;


function getDefaultTabMetaFilter(tab: Tab) {
    const filter = {};
    if (!tab) return filter;
    if (isValidDayNumber(tab.max_days_in_facility)) {
        const arrival_date = dayjs().subtract(tab.max_days_in_facility as number, "day");
        filter["layer_meta.arrival_date_intake_date_from"] = {
            label: `Arrival date > ${arrival_date.format("DD-MM-YYYY")}`,
            field: "layer_meta.arrival_date_intake",
            optional: false,
            method: FILTER_METHODS.DATE_FROM,
            value: arrival_date.format("YYYY-MM-DD"),
        };
    }
    if (isValidDayNumber(tab.planned_work_look_ahead_days)) {
        const planned_check_date = dayjs().add(tab.planned_work_look_ahead_days as number, "day");
        filter["layer_meta.planned_check_date_date_to"] = {
            label: `Planned work date < ${planned_check_date.format("DD-MM-YYYY")}`,
            field: "layer_meta.planned_check_date",
            optional: false,
            method: FILTER_METHODS.DATE_TO,
            value: planned_check_date.format("YYYY-MM-DD"),
        };
    }
    if (isValidDayNumber(tab.max_days_in_stage)) {
        filter["latest_location.days_in_stage_max"] = {
            label: `Days in Stage ≤ ${tab.max_days_in_stage}`,
            field: "latest_location.days_in_stage",
            optional: false,
            method: FILTER_METHODS.NUMBER_RANGE_MAX,
            value: Number(tab.max_days_in_stage),
        };
    }
    if (isFilledArray(tab.show_draft_reports)) {
        filter["layer_meta.qc_status_draft_reports"] = {
            label: `Items with Draft reports`,
            field: tab.show_draft_reports,
            optional: false,
            editable: false,
            method: FILTER_METHODS.IN_ANY_KEY,
            value: [QC_STATUS.DRAFT],
        };
    }
    if (isFilledArray(tab.show_sent_reports)) {
        filter["layer_meta.qc_status_sent_reports"] = {
            label: `Items with Sent reports`,
            field: tab.show_sent_reports,
            optional: false,
            editable: false,
            method: FILTER_METHODS.IN_ANY_KEY,
            value: [QC_STATUS.SENT],
        };
    }
    if (attributeIsSet(tab.show_layers_without_planned_work)) {
        if (tab.show_layers_without_planned_work === "all") {
            filter["layer_meta.planned_check_date_is_set"] = null;
        } else {
            filter["layer_meta.planned_check_date_is_set"] = {
                label: tab.show_layers_without_planned_work === "only-filled" ? "Items with Planned work date" : "Items without Planned work date",
                field: "layer_meta.planned_check_date",
                optional: false,
                method: FILTER_METHODS.IS_SET,
                value: tab.show_layers_without_planned_work,
            };
        }
    }
    if (isFilledArray(tab.filter_layer_meta)) {
        tab.filter_layer_meta!.forEach((item) => {
            // * "parent_meta" matches the meta of an active parent instead of the layer itself
            const source = item.source === META_FILTER_SOURCES.PARENT_META ? META_FILTER_SOURCES.PARENT_META : META_FILTER_SOURCES.LAYER_META;
            const key = `${source}.${item.field}_${item.method}`;
            const needsArray = item.method === FILTER_METHODS.IN || item.method === FILTER_METHODS.NOT_IN;
            const arrayValue = Array.isArray(item.value) ? item.value : String(item.value).split(",").map((v) => v.trim());
            const value = needsArray ? arrayValue : item.value;
            const label = item.method === FILTER_METHODS.NOT_CURRENT_FACILITY
                ? `${item.field}: not current facility`
                : `${item.field}: ${Array.isArray(value) ? value.join(", ") : value}`;
            filter[key] = {
                label,
                field: `${source}.${item.field}`,
                optional: false,
                hidden: true,
                method: item.method,
                value,
            };
        });
    }

    return filter;
}
// Provider component
export const SwitchTabProvider = ({ children }) => {
    const config = useConfig();
    const queryClient = useQueryClient();
    const [userPreferences, updatePreferences] = useUserPreferences();
    const selectedFruitTypes = userPreferences.fruitTypes;
    const selectedFacilities = userPreferences.facilities;
    const selectedAssignedTeams = userPreferences.assignedTeam;

    const params = useParams();
    const { active_tab } = params;
    const navigate = useTenantNavigate();
    const auth = useAuthorization();
    const [searchParams] = useSearchParams();
    const [searchQuery, setSearchQuery] = useState("");
    const { applyDisplayFilterLayerFields } = useFields();

    const { offset, limit } = usePagination();

    const [selectedLayers, setSelectedLayers] = useState({});
    const selectedLayersArray = Object.keys(selectedLayers).filter((key) => selectedLayers[key]);

    // * Tab settings and full query (ordering + meta_filter) per tab; persisted in user preferences
    const [personalTabSettingsMap, setPersonalTabSettings] = useState(userPreferences?.tabs || {} as {[tab: string]: PersonalTabSettings});
    const personalTabSettings = personalTabSettingsMap[active_tab as string] || {};
    const personalTabQuery = (personalTabSettingsMap[active_tab as string]?.query as Record<string, unknown>) || {};
    const { fromDateParam, toDateParam } = useRipeningPlanningDateRange();

    // * Setting up tabs
    const tabs = config.applyDisplayFilter(config.stages, { facilities: selectedFacilities }).map((i) => ({
        ...i,
        title: i.text,
        tab_categories: getFilledArrayOrDefault(i.tab_categories, [LAYER_TAB_CATEGORIES.OPERATION]),
        subtitle: null,
        value: i.value,

        layer_type: personalTabSettingsMap[i.value]?.layer_type || i.layer_type,
        view_strategy: personalTabSettingsMap[i.value]?.view_strategy || i.view_strategy,
        ordering: personalTabSettingsMap[i.value]?.query?.ordering || personalTabSettingsMap[i.value]?.ordering || i.default_ordering,

        // * Tab attributes that can be translated to a meta query with getDefaultTabMetaFilter
        max_days_in_facility: personalTabSettingsMap[i.value]?.max_days_in_facility || i.max_days_in_facility,
        planned_work_look_ahead_days: personalTabSettingsMap[i.value]?.planned_work_look_ahead_days || i.planned_work_look_ahead_days,
        max_days_in_stage: personalTabSettingsMap[i.value]?.max_days_in_stage || i.max_days_in_stage,
        show_draft_reports: personalTabSettingsMap[i.value]?.show_draft_reports || i.show_draft_reports,
        show_sent_reports: personalTabSettingsMap[i.value]?.show_sent_reports || i.show_sent_reports,
        show_layers_without_planned_work: personalTabSettingsMap[i.value]?.show_layers_without_planned_work || i.show_layers_without_planned_work,
        default_ordering: i.default_ordering,

    })).filter((i) => {
        if (isFilledArray(i.show_for_teams)) {
            return auth.userBelongsToOneOfTeams(i.show_for_teams);
        }
        if (isFilledArray(i.hide_for_teams)) {
            // * hide tab if user belongs to one of the teams
            return !auth.userBelongsToOneOfTeams(i.hide_for_teams);
        }
        return true;
    });

    const tab_category = params.tab_category || tabs?.[0]?.tab_categories?.[0] || LAYER_TAB_CATEGORIES.OPERATION;
    const sameCategoryTabs = tabs.filter((i) => i.tab_categories.includes(tab_category));
    const activeTab = tabs.find((i) => i.value === active_tab) || sameCategoryTabs?.[0] || tabs?.[0];

    const ripeningPlanning = useRipeningPlanning(activeTab);
    const { getExtraFields, ripeningFieldName } = ripeningPlanning;
    // * Get fields for the active tab
    const getFields = (tab, selectedFruitTypes) => {
        if (!tab) return [];
        return applyDisplayFilterLayerFields(tab?.layer_overview_fields || [], {
            fruit_type: getFilledArrayOrDefault(selectedFruitTypes).length === 1 ? selectedFruitTypes[0] : null, // * only enable fruit type filter on fields when exactly one fruit type is selected
            layer_type: tab?.layer_type
        });
    };

    const fields = useMemo(() => getFields(activeTab, selectedFruitTypes), [activeTab?.value, selectedFruitTypes]);
    // * Fields included in Excel download (excluded_from_download = false); used by streamLayersForExport and DownloadExcelButton
    const downloadFields = useMemo(
        () => getFilledArrayOrDefault(fields).filter((field) => !field.excluded_from_download),
        [fields]
    );

    // * Define functions for fetching layers
    // * `filter` follows the UI immediately; `queryFilter` lags behind it by the debounce and drives the fetch.
    const [filter, setFilter] = useState<LayerFilter>(defaultFilter);
    const [queryFilter, setQueryFilter] = useState<LayerFilter | null>(null);
    const layerListQuery = useLayerList(config.tenant_id, queryFilter);
    const layers = layerListQuery.data ?? { results: [] };
    // isPending, not isFetching: it stays true while the tab is still being
    // composed (query disabled) but goes false once rows exist, so a background
    // refetch on window focus no longer blanks the table.
    const layersLoading = layerListQuery.isPending;

    const [listLayersDebounced] = useDebounce(
        (newFilter: LayerFilter, extendFilter = true) => setFilter((prev) => (extendFilter ? { ...prev, ...newFilter } : newFilter)),
        (newFilter: LayerFilter, extendFilter = true) => setQueryFilter((prev) => (extendFilter && prev ? { ...prev, ...newFilter } : newFilter)),
        { debounceTime: 500, immediate: true }
    );

    const buildFilterFromQuery = (query) => {
        if (!activeTab) return null;
        const { ordering, ...userMetaFilter } = query;
        // * Get default meta_filter for this tab
        const defaultTabFilter = getDefaultTabMetaFilter(activeTab);
        // * Make sure default filters can only be overwritten by non-null values (you can edit a default filter, but never completely remove it)
        const meta_filter = Object.keys(userMetaFilter).reduce((acc, i) => {
            if (userMetaFilter[i]) {
                acc[i] = userMetaFilter[i];
            }
            return acc;
        }, defaultTabFilter);
        // * Get table fields for the active tab
        const facilityFilter = isFilledArray(activeTab.filter_facilities) ? activeTab.filter_facilities : undefined;
        const fields = getFields(activeTab, selectedFruitTypes).flatMap((i: Field) => i.get_backend_fieldname());
        // * Get extra fields for the ripening class distribution
        const extraFields = getExtraFields(activeTab);
        const select_fields = [...fields, ...extraFields];
        const newFilter: LayerFilter = {
            stage: activeTab.filter_stages,
            assigned_team: selectedAssignedTeams.length > 0 ? selectedAssignedTeams : undefined,
            check_location: activeTab.filter_check_locations,
            layer_type: activeTab.layer_type,
            view_strategy: activeTab.view_strategy,
            fruit_type: selectedFruitTypes,
            facility: facilityFilter || (selectedFacilities.length > 0 ? selectedFacilities : undefined),
            facility_filter_meta_fields: activeTab.facility_filter_meta_fields,
            meta_filter,
            select_fields,
            offset,
            query: searchParams.get("query") || undefined,
            limit: query.limit ?? limit,
            ordering: ordering || activeTab.ordering || activeTab.default_ordering || "check.created,DESC",

        };
        return newFilter;
    };

    const buildQueryAndlistLayers = (query) => {
        const newFilter = buildFilterFromQuery(query);
        if (newFilter) listLayersDebounced(newFilter, false);
    };

    const streamLayersForExport = useCallback(async function* streamExport(exportOptions: {
        limit?: number;
        chunk_size?: number;
    }) {
        if (!activeTab) return;
        const filter = buildFilterFromQuery(personalTabQuery);
        if (!filter) return;
        // * Export uses offset 0 and downloadFields (columns not excluded from download)
        filter.offset = 0;
        filter.select_fields = downloadFields.flatMap((f: Field) => f.get_backend_fieldname());
        filter.chunk_size = exportOptions.chunk_size;
        filter.limit = exportOptions.limit;
        const params = prepareParams(filter);
        const stream = streamAvosApi("/layers/streaming", { params });
        yield* stream;
    }, [activeTab, personalTabQuery, downloadFields, selectedFruitTypes, selectedFacilities, selectedAssignedTeams, searchParams, limit, offset]);

    const updatePersonalTabSettings = useCallback((field, value = null) => {
        setPersonalTabSettings((prev) => {
            const newSettings = typeof field === "object"
                ? { ...prev[activeTab?.value as string], ...field }
                : { ...prev[activeTab?.value as string], [field]: value };
            const tabs = ({ ...prev, [activeTab?.value as string]: newSettings });

            // * Store tab settings in preferences
            updatePreferences({
                tabs
            });

            return tabs;
        });
    }, [activeTab?.value, userPreferences]);

    // * Submit tab query (filter modal or ordering change); persists in user preferences
    const submitTabQuery = useCallback((query) => {
        if (!activeTab?.value) return;
        updatePersonalTabSettings({ query });
        normalizeUrl();
        // * buildQueryAndlistLayers will be called by the effect when userPreferences.tabs changes
    }, [activeTab?.value, updatePersonalTabSettings]);

    // * Update ordering; persists in user preferences
    const onUpdateOrder = (ordering: string) => {
        const currentQuery = personalTabQuery || {};
        submitTabQuery({ ...currentQuery, ordering });
    };

    const refreshTab = () => {
        if (!active_tab || !activeTab) return;
        const query = (personalTabSettingsMap[activeTab.value]?.query as Record<string, unknown>) || {};
        buildQueryAndlistLayers(query);
        // * The rebuilt filter is usually identical, so nudge the cache to actually refetch
        queryClient.invalidateQueries({ queryKey: layerListQueryKey(config.tenant_id) });
    };

    // * Effect when you change tab or fruit types or layer_type
    useEffect(() => {
        setQueryFilter(null);
        setSelectedLayers({});
        if (!active_tab || activeTab?.value !== active_tab || active_tab === "first") {
            return;
        }

        if (activeTab?.value) {
            // * Special case for packing list and mailbox
            if ([LAYER_TABS.PACKING_LIST, LAYER_TABS.QC_MAILBOX].includes(activeTab.value)) {
                return;
            }
            const query = (personalTabSettingsMap[activeTab.value]?.query as Record<string, unknown>) || {};
            buildQueryAndlistLayers(query);
        }
    }, [config?.tenant_id, active_tab, activeTab?.value, selectedFruitTypes.join(","), selectedFacilities.join(","), selectedAssignedTeams.join(","), JSON.stringify(userPreferences?.tabs), searchQuery, offset, limit, ripeningFieldName, fromDateParam, toDateParam]);


    const switchTab = (tab) => {
        navigate(`/layer/tab/${tab_category || tab.tab_categories[0]}/${tab.value}`);
    };
    const normalizeUrl = useCallback(() => navigate(`/layer/tab/${tab_category}/${activeTab?.value}`, { replace: true }), [activeTab?.category, activeTab?.value]);

    // * Auto-navigate to first tab if no active_tab in URL
    useEffect(() => {
        if ((!active_tab || active_tab === "first") && activeTab?.value) {
            navigate(`/layer/tab/${tab_category}/${activeTab.value}`, { replace: true });
        }
    }, [active_tab, activeTab?.value, tab_category, navigate]);

    // * Group By Properties
    const group_by_fields = applyDisplayFilterFields(activeTab?.group_by_fields, {
        fruit_type: getFilledArrayOrDefault(selectedFruitTypes).length === 1 ? selectedFruitTypes[0] : null, // * only enable fruit type filter on fields when exactly one fruit type is selected
        layer_type: activeTab?.layer_type
    });
    const groupByProps = useGroupBy(group_by_fields, activeTab?.group_by_default);

    return (
        <SwitchTabContext.Provider value={{
            switchTab,
            listLayersDebounced,
            filter,
            layers,
            layersLoading,
            tabs,
            sameCategoryTabs,
            activeTab,
            active_tab,
            tab_category,
            fields,
            downloadFields,
            submitTabQuery,
            onUpdateOrder,
            streamLayersForExport,
            personalTabQuery,
            personalTabSettings,
            updatePersonalTabSettings,
            normalizeUrl,
            selectedLayers,
            setSelectedLayers,
            selectedLayersArray,
            refreshTab,
            groupByProps,
            setSearchQuery,
            ripeningPlanning,
        }}>
            {children}
        </SwitchTabContext.Provider>
    );
};
